import React from 'react';
import {scaleLog} from 'd3-scale';
import {
  INITIAL_STATE,
  GEL_PATH_MIN_HEIGHT,
  COL_TYPE,
  CELL_TYPE,
  GEL_DRAG_DROP,
} from './GelDragDrop.constants';

export function getScale(state) {
  return scaleLog()
    .base(state.logBase)
    .domain([state.rangeBottom, state.rangeTop])
    .range([GEL_PATH_MIN_HEIGHT, state.gelPathHeight]);
}

export const GelDragDropContext = React.createContext(null);

export const GelTableRef = React.createRef(null);

export function getTop(scaleValue, state) {
  return state.gelPathHeight - scaleValue + state.gelPathPadding;
}

export function useGelDragDrop(props) {
  const [state, setState] = React.useState({
    ...INITIAL_STATE,
    rangeTop: props.gelRange.top,
    rangeBottom: props.gelRange.bottom,
  });
  const [topValues, setTopValues] = React.useState({min: 1, max: 1});

  // To calculate and set top values whenever state changes.
  React.useEffect(() => {
    const scale = getScale(state);
    const min = getTop(scale(state.rangeTop), state);
    const max = getTop(scale(state.rangeBottom), state);
    setTopValues({min, max});
  }, [state.gelPathHeight]);

  // function to get the max number of Gels in any holders of table
  const maxGelItemsPerHolder = React.useMemo(
    () => getMaxGelItemsPerHolder(props.gelTable),
    []
  );

  // Function to calculate Gel-Table Dimensions
  function calculateHeight(tableRef) {
    const {offsetWidth} = tableRef;
    let newState = {...state};
    newState.gelPathHeight = getGelPathHeight(offsetWidth);
    newState.gelHolderBoxHeight = getGelBoxHeight(newState.gelPathHeight);
    newState.gelPathPadding = getGelPathPaddingBottom(
      newState.gelHolderBoxHeight
    );
    newState.trashRowHeight = getTrashRowHeight(
      newState.gelHolderBoxHeight,
      maxGelItemsPerHolder
    );
    setState(newState);
  }

  // Function to allow dagging over other elements
  const onDragOver = (event) => event.preventDefault();

  // Function to calculate new-gel-value
  const getGelValue = ({currentTarget, clientY}) => {
    if (!currentTarget) return;
    const {top} = currentTarget.getBoundingClientRect();
    const scale = getScale(state);
    let topCurrent = clientY - top;
    if (topCurrent < topValues.min) topCurrent = topValues.min;
    if (topCurrent > topValues.max) topCurrent = topValues.max;
    const logCurrent = getLogValue(topCurrent, state);
    const valuecurrent = scale.invert(logCurrent);
    return valuecurrent;
  };

  // Function to invoke on GelStripDrop
  const onStripDrop = ({event, value, cellId, label}) => {
    let [colId, prevCellId, gelId] = splitIDs(
      event.dataTransfer.getData(GEL_DRAG_DROP.GEL_ID)
    );
    let [currentColId, currentCellId] = splitIDs(cellId);
    if (colId === currentColId) {
      props.onGelUpdate({
        colId,
        prevCellId,
        currentCellId,
        gelId,
        value,
        label,
      });
    }
  };

  // Function to invoke when GelBand is moved by keyboard
  const onStripChange = ({colId, prevCellId, gelId, currentCellId, value}) => {
    props.onGelUpdate({
      colId,
      prevCellId,
      currentCellId,
      gelId,
      value,
    });
  };

  const onDropDownChange = ({
    colId,
    prevCellId,
    gelId,
    currentCellId,
    label,
    value,
  }) => {
    props.onDropDownUpdate({
      colId,
      prevCellId,
      currentCellId,
      gelId,
      label,
      value,
    });
  };

  // Function to set style grid templateRows in column componnets
  const getGridTemplateRows = (colItemsLength) => {
    let value = '';
    for (let i = 0; i < colItemsLength; i++) {
      value += 'auto ';
    }
    return {gridTemplateRows: value};
  };

  // Variable with a list of all clipable values
  const clipValues = React.useMemo(() => {
    let ladderValues = [];
    if (props.gelTable[0].colType === COL_TYPE.LABEL_COLUMN) {
      ladderValues = props.gelTable[0].colItems[2].cellItems
        .map((gel) => gel.value)
        .sort((a, b) => a - b);
    }
    if (props.gelTable[0].colType === COL_TYPE.LADDER_COLUMN) {
      ladderValues = props.gelTable[0].colItems[1].cellItems
        .map((gel) => gel.value)
        .sort((a, b) => a - b);
    }
    return ladderValues.flatMap((value, index) => {
      if (index >= 1) {
        return [(ladderValues[index - 1] + value) / 2, value];
      }
      return [value];
    });
  }, []);

  // Funtion to invoke when a gel band is clicked
  const onGelBandClick = (idString) => {
    const [colId, cellId, gelId] = splitIDs(idString);
    props.onGelSelect({colId, cellId, gelId});
  };

  return {
    state,
    clipValues,
    isMultiGelHolder: maxGelItemsPerHolder > 1,
    calculateHeight,
    onDragOver,
    getGelValue,
    onStripDrop,
    getGridTemplateRows,
    onStripChange,
    onDropDownChange,
    onGelBandClick,
  };
}

function getGelPathHeight(offsetWidth) {
  return offsetWidth / 1.15;
}

function getGelBoxHeight(gelPathHeight) {
  return gelPathHeight * 0.03;
}

function getGelPathPaddingBottom(gelHolderBoxHeight) {
  return gelHolderBoxHeight * 2;
}

function getTrashRowHeight(holderBoxHeight, maxItems) {
  return holderBoxHeight * (maxItems + 1);
}

function getMaxGelItemsPerHolder(gelTable) {
  return gelTable.reduce((max, column) => {
    if (column.colType === COL_TYPE.BAND_COLUMN) {
      let newMax = column.colItems.reduce((maxItems, cell) => {
        if (cell.cellType === CELL_TYPE.GEL_HOLDER) {
          maxItems = cell.cellItems.length;
        }
        return maxItems;
      }, 0);
      if (newMax > max) {
        max = newMax;
      }
    }
    return max;
  }, 1);
}

export function getTopValue(scaleValue, state) {
  return state.gelPathHeight - scaleValue + state.gelPathPadding;
}

function getLogValue(topValue, state) {
  return getTopValue(topValue, state);
}

export function splitIDs(idString) {
  return idString.split('_').map((numString) => parseInt(numString));
}

const CELL_IDS = {
  1: CELL_TYPE.GEL_HOLDER,
  2: CELL_TYPE.GEL_PATH,
  3: CELL_TYPE.TRASH_CAN,
};

export function getCellTypeFromID(gelId) {
  const cellId = splitIDs(gelId)[1];
  return {cellType: CELL_IDS[cellId], cellId};
}

export function getNextClipValue(clipValues, currentValue) {
  let index = clipValues.findIndex((clipValue) => clipValue === currentValue);
  return clipValues[index + 1];
}
export function getPreviousClipValue(clipValues, currentValue) {
  let index = clipValues.findIndex((clipValue) => clipValue === currentValue);
  return clipValues[index - 1];
}
